import Close from '@mui/icons-material/Close'
import { Box, Button, Grid, Modal, Typography } from '@mui/material'
import React, { useState } from 'react'
import type { ExclusionAreaObservation } from '../../../../types/apiModels'
import { ExclusionAreaObservationModal } from './ExclusionAreaObservationModal'
import { ExclusionAreaObservationsAccordions } from './ExclusionAreaObservationsAccordions'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  height: '80%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  textAlign: 'center',
  bgcolor: 'background.paper',
  borderRadius: 4,
  boxShadow: 24,
  p: 1,
}

interface ExclusionAreaObservationsModalProps {
  open: boolean
  onClose: () => void
  observations: ExclusionAreaObservation[]
  canCreate: boolean
  canResolve: boolean
  canComment: boolean
  onCreateObservation: (data: { type: number; comment: string }) => Promise<unknown>
  onResolve: (observationId: string) => void
}

export const ExclusionAreaObservationsModal: React.FC<ExclusionAreaObservationsModalProps> = ({
  open,
  onClose,
  observations,
  canCreate,
  canResolve,
  canComment,
  onCreateObservation,
  onResolve,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  const handleCreate = async (data: { type: number; comment: string }) => {
    setCreating(true)
    try {
      await onCreateObservation(data)
      setCreateModalOpen(false)
    } catch {
      // The global mutation snackbar already showed the backend message (e.g. the
      // Auditor-only role gate); the form stays open so the user can correct and retry.
    } finally {
      setCreating(false)
    }
  }

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <Grid container alignItems="center" pt={0.5}>
            <Grid item xs={canCreate ? 7 : 10}>
              <Typography mb={0.5} pl={8} variant="h6">
                Observaciones del Área de Exclusión
              </Typography>
            </Grid>
            {canCreate && (
              <Grid item xs={3}>
                <Button
                  size="small"
                  sx={{ textTransform: 'none' }}
                  variant="contained"
                  onClick={() => setCreateModalOpen(true)}
                >
                  Nueva observación
                </Button>
              </Grid>
            )}
            <Grid item px={1} xs={2}>
              <Close
                sx={{ float: 'right', cursor: 'pointer', '&:hover': { color: 'red' } }}
                onClick={onClose}
              />
            </Grid>
          </Grid>

          {observations.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                overflow: 'scroll',
                height: '100%',
              }}
            >
              <ExclusionAreaObservationsAccordions
                canComment={canComment}
                canResolve={canResolve}
                observations={observations}
                onResolve={onResolve}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                flexDirection: 'column',
                padding: 1,
                margin: 1,
              }}
            >
              <Typography color="gray" my={2} variant="h4">
                No hay observaciones
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>

      {createModalOpen && (
        <ExclusionAreaObservationModal
          loading={creating}
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onConfirm={handleCreate}
        />
      )}
    </>
  )
}
